"use client";

import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import DATA from "@/lib/hp-nights-data.json";
import { truncateChars } from "@shared/text";

/** Lazy receipts store: fact id → its source episodes (book quotes redacted at export). */
type Receipt = { ch: number; sal: number; text: string };
let receiptsCache: Record<string, Receipt[]> | null = null;
const loadReceipts = async (): Promise<Record<string, Receipt[]>> => {
  receiptsCache ??= ((await import("@/lib/hp-receipts-data.json")) as { receipts: Record<string, Receipt[]> }).receipts;
  return receiptsCache;
};

/** Every episode the store holds, including the ones no belief was built from. */
type Episode = { ch: number; sal: number; used: boolean; text: string };
let episodesCache: Episode[] | null = null;
const loadEpisodes = async (): Promise<Episode[]> => {
  episodesCache ??= ((await import("@/lib/hp-episodes-data.json")) as { episodes: Episode[] }).episodes;
  return episodesCache;
};

const REDACTION = "[quote redacted]";

/** Render receipt text with redaction markers as visible struck chips. */
function RedactedText({ text }: { text: string }) {
  const parts = text.split(REDACTION);
  return (
    <>
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 && (
            <span className="mx-0.5 rounded-sm bg-foreground/10 px-1.5 font-mono text-[10px] uppercase tracking-[0.06em] text-foreground-muted">
              quote redacted
            </span>
          )}
          {p}
        </span>
      ))}
    </>
  );
}

/**
 * Interactive figure for the book-memory showcase post: one novel read a
 * chapter per day, consolidated nightly by the production memory library.
 * Scrub through the nights to watch beliefs form, sharpen, and get overturned.
 *
 * Data contract (lib/hp-nights-data.json, regenerated from the run's
 * per-chapter snapshots — see the chapter manifest in the eval artifacts):
 *   facts[]:   { id, kind, sal, born, closed|null, by|null, content, edges }
 *              born/closed are 1-based night numbers; `by` = successor fact id.
 *   threads[]: { name, ids[] } — supersede lineages worth following.
 *   counts[]:  { ch, open, total } per night.
 * The night count is derived from counts.length — 16 vs 17 chapters needs no
 * code change here.
 */

interface HpFact {
  id: string;
  kind: string;
  sal: number;
  born: number;
  closed: number | null;
  by: string | null;
  /** Why the fact closed: overturned (contradicted) / merged / completed / expired. */
  why: "overturned" | "merged" | "completed" | "expired" | null;
  content: string;
  edges: number;
  /** True when OUR redaction left the text unreadable — the row is kept, but says so. */
  redacted?: boolean;
  /** Sparse wording history: the store enriches fact text in place, so the figure
   *  shows the wording as it stood after the selected chapter. */
  versions?: { ch: number; content: string }[];
  /** Chapter of each evidence episode, ascending. A belief keeps gaining evidence
   *  after it is born, so receipts are shown as of the selected chapter — otherwise
   *  a chapter-1 belief displays chapter-2 memories and reads as a plot leak. */
  edgeChs?: number[];
}

/** The fact's wording as of a given night (falls back to final wording). */
const wordingAt = (f: HpFact, night: number): string => {
  if (!f.versions) return f.content;
  const past = f.versions.filter((v) => v.ch <= night);
  return past.length > 0 ? past[past.length - 1].content : f.versions[0].content;
};
interface HpThread {
  name: string;
  ids: string[];
}
const FACTS = DATA.facts as HpFact[];
const THREADS = DATA.threads as HpThread[];
const COUNTS = DATA.counts as { ch: number; open: number; total: number }[];
const NIGHTS = COUNTS.length;
const BY_ID = new Map(FACTS.map((f) => [f.id, f]));
const THREAD_OF = new Map<string, string>(THREADS.flatMap((t) => t.ids.map((id) => [id, t.name] as const)));

function FactRow({ f, night, focusThread, onJump }: { f: HpFact; night: number; focusThread: string | null; onJump: (id: string) => void }) {
  const isNew = f.born === night;
  const diedNow = f.closed === night;
  const dead = f.closed !== null && f.closed <= night;
  const succ = f.by ? BY_ID.get(f.by) : undefined;
  const threadName = THREAD_OF.get(f.id);
  const [receipts, setReceipts] = useState<Receipt[] | null>(null);
  const [open, setOpen] = useState(false);
  // Evidence as it stood after the selected chapter, not the final tally.
  const edgesNow = f.edgeChs ? f.edgeChs.filter((c) => c <= night).length : f.edges;
  const receiptsNow = receipts?.filter((r) => r.ch <= night) ?? null;
  const toggleReceipts = async () => {
    if (!open && receipts === null) {
      const all = await loadReceipts();
      setReceipts(all[f.id] ?? []);
    }
    setOpen((o) => !o);
  };
  return (
    <div
      id={`hpfact-${f.id}`}
      className={`flex flex-col gap-1.5 py-3 ${dead ? "opacity-60" : ""}`}
    >
      {f.redacted ? (
        <p className="font-serif text-[15px] italic leading-relaxed text-foreground-muted">
          This memory quoted the book too closely to publish, so we removed it. The source memories below are the agent&rsquo;s own words.
        </p>
      ) : (
        // overturned vs merely closed is the whole argument, so they must not
        // look alike: contradicted beliefs are struck through, closed ones fade
        <p className={`font-serif text-[15px] leading-relaxed ${dead ? (f.why === "overturned" ? "line-through decoration-1 decoration-destructive/60" : "italic") : ""}`}>
          <RedactedText text={wordingAt(f, night)} />
        </p>
      )}
      {f.versions && f.versions.some((v) => v.ch > night) && (
        <p className="font-mono text-[10px] uppercase tracking-[0.06em] text-foreground-muted">
          wording changes later
        </p>
      )}
      {diedNow && succ && (
        <p className="text-left text-xs text-foreground-muted">
          →{" "}
          <button type="button" onClick={() => onJump(succ.id)} className="text-success underline decoration-dotted">
            {truncateChars(succ.content.split(REDACTION).join("…"), 90)}…
          </button>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2.5">
        {/* "What changed" lists facts born AND facts closed this chapter, so the
            badge has to follow the fact, not the mode — a belief overturned tonight
            is not new tonight. */}
        {isNew && <span className="font-mono text-[10px] uppercase tracking-[0.07em] text-primary">new</span>}
        {dead && f.why && (
          <span className={`font-mono text-[10px] uppercase tracking-[0.07em] ${f.why === "overturned" ? "text-destructive" : "text-foreground-muted"}`}>
            {f.why}
          </span>
        )}
        <span className="font-mono text-[10px] uppercase tracking-[0.07em] text-foreground-muted">{f.kind}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.07em] tabular-nums text-foreground-muted">
          salience {f.sal.toFixed(2)}
        </span>
        <button
          type="button"
          onClick={() => void toggleReceipts()}
          aria-expanded={open}
          className="font-mono text-[11px] tabular-nums text-foreground-muted underline decoration-dotted underline-offset-2 hover:text-foreground"
        >
          distilled from {edgesNow} episodic {edgesNow === 1 ? "memory" : "memories"} ·{" "}
          ch. {f.born}
          {/* only once it has happened — the range must not announce a closure
              the reader has not reached yet */}
          {f.closed !== null && f.closed <= night ? `–${Math.min(NIGHTS, f.closed)}` : ""}
        </button>
        {threadName && focusThread === threadName && (
          <span className="font-mono text-[10px] uppercase tracking-[0.05em] text-success">{threadName}</span>
        )}
      </div>
      {open && receiptsNow && (
        <div className="mt-1 flex flex-col gap-2 border-l-2 border-border-subtle pl-3">
          {receiptsNow.map((r, i) => {
            // Some memories were quotation end to end, so redaction leaves nothing
            // but the marker. Say that, rather than printing a bare chip twice.
            const gutted = r.text.split(REDACTION).join("").trim() === "";
            return (
              <p key={i} className="text-[13px] leading-relaxed text-foreground-muted">
                <span className="mr-1.5 font-mono text-[10px] uppercase tracking-[0.06em]">ch. {r.ch}</span>
                {gutted ? <em>this memory was quotation end to end, so nothing of it survives here</em> : <RedactedText text={r.text} />}
              </p>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Spark({ night, setNight }: { night: number; setNight: Dispatch<SetStateAction<number>> }) {
  const W = 960;
  const H = 132;
  const P = 8;
  const LABEL = 14;
  const max = Math.max(...COUNTS.map((c) => c.total));
  const slot = (W - 2 * P) / NIGHTS;
  const gap = Math.min(6, slot * 0.18);
  const x = (i: number) => P + slot * i + gap / 2;
  const barH = (v: number) => (v / max) * (H - LABEL - 26);
  const pick = (clientX: number, el: SVGSVGElement) => {
    const r = el.getBoundingClientRect();
    const fx = ((clientX - r.left) / r.width) * W;
    setNight(Math.min(NIGHTS, Math.max(1, Math.floor((fx - P) / slot) + 1)));
  };
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="block h-[132px] w-full cursor-pointer"
      role="slider"
      aria-label="chapter selector"
      aria-valuemin={1}
      aria-valuemax={NIGHTS}
      aria-valuenow={night}
      tabIndex={0}
      onClick={(e) => pick(e.clientX, e.currentTarget)}
      onMouseMove={(e) => e.buttons === 1 && pick(e.clientX, e.currentTarget)}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") setNight((n) => Math.min(NIGHTS, n + 1));
        if (e.key === "ArrowLeft") setNight((n) => Math.max(1, n - 1));
        if (e.key === "Home") setNight(1);
        if (e.key === "End") setNight(NIGHTS);
      }}
    >
      {COUNTS.map((c, i) => {
        const sel = i === night - 1;
        const h = Math.max(2, barH(c.total));
        return (
          <rect
            key={c.ch}
            x={x(i)}
            y={H - LABEL - h}
            width={slot - gap}
            height={h}
            rx="2"
            className={sel ? "fill-primary" : "fill-success/35"}
          />
        );
      })}
      {COUNTS.map((c, i) => (
        <text
          key={c.ch}
          x={x(i) + (slot - gap) / 2}
          y={H - 2}
          textAnchor="middle"
          className={`text-[9px] tabular-nums ${i === night - 1 ? "fill-primary" : "fill-foreground-muted"}`}
        >
          {i + 1}
        </text>
      ))}
    </svg>
  );
}

export function FigHpNights() {
  const [night, setNight] = useState(1);
  const [mode, setMode] = useState<"diff" | "all" | "eps">("diff");
  const [focus, setFocus] = useState<string>("");
  const [episodes, setEpisodes] = useState<Episode[] | null>(null);

  const showEpisodes = () => {
    setMode("eps");
    if (episodes === null) void loadEpisodes().then(setEpisodes);
  };
  const nightEpisodes = episodes?.filter((e) => e.ch === night) ?? [];

  const focusThread = focus.startsWith("t:") ? focus.slice(2) : null;
  const focusKind = focus.startsWith("k:") ? focus.slice(2) : null;

  const shown = useMemo(() => {
    if (mode === "eps") return [];
    let fs = FACTS.filter((f) => f.born <= night);
    fs = mode === "diff" ? fs.filter((f) => f.born === night || f.closed === night) : fs.filter((f) => !f.closed || f.closed >= night);
    if (focusKind) fs = fs.filter((f) => f.kind === focusKind);
    if (focusThread) fs = fs.filter((f) => THREAD_OF.get(f.id) === focusThread);
    return mode === "diff"
      ? fs.sort((a, b) => b.sal - a.sal)
      : fs.sort((a, b) => Number(b.born === night) - Number(a.born === night) || b.sal - a.sal);
  }, [night, mode, focusKind, focusThread]);

  /** First chapter the current selection has anything in, so an empty panel can say so. */
  const firstChapterOfFocus = useMemo(() => {
    const pool = focusThread
      ? FACTS.filter((f) => THREAD_OF.get(f.id) === focusThread)
      : focusKind
        ? FACTS.filter((f) => f.kind === focusKind)
        : [];
    return pool.length > 0 ? Math.min(...pool.map((f) => f.born)) : null;
  }, [focusThread, focusKind]);

  const jump = (id: string) => {
    const f = BY_ID.get(id);
    if (!f) return;
    setNight((n) => Math.max(n, f.born));
    setMode("all");
    requestAnimationFrame(() => document.getElementById(`hpfact-${id}`)?.scrollIntoView({ block: "center", behavior: "smooth" }));
  };

  const c = COUNTS[night - 1];
  return (
    <figure className="overflow-hidden rounded-xl border border-border-subtle bg-surface-elevated">
      <div className="p-5 sm:p-6">
        <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.08em] text-foreground-muted">
          beliefs held, by chapter
        </p>
        <Spark night={night} setNight={setNight} />
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="inline-flex overflow-hidden rounded-md border border-border-subtle">
            <button
              type="button"
              onClick={() => setNight((n) => Math.max(1, n - 1))}
              disabled={night === 1}
              aria-label="previous chapter"
              className="px-2.5 py-1 text-sm text-foreground-muted enabled:hover:bg-accent disabled:opacity-40"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => setNight((n) => Math.min(NIGHTS, n + 1))}
              disabled={night === NIGHTS}
              aria-label="next chapter"
              className="border-l border-border-subtle px-2.5 py-1 text-sm text-foreground-muted enabled:hover:bg-accent disabled:opacity-40"
            >
              ›
            </button>
          </span>
          <span className="whitespace-nowrap font-serif text-xl font-semibold">After chapter {night}</span>
          <span className="w-full text-sm tabular-nums text-foreground-muted sm:w-auto">
            {c.open} live beliefs · {FACTS.filter((f) => f.closed !== null && f.closed <= night && f.why === "overturned").length} overturned
            · {FACTS.filter((f) => f.closed !== null && f.closed <= night && f.why !== "overturned").length} merged, completed or expired
          </span>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 border-y border-border-subtle py-2.5">
          <span className="inline-flex overflow-hidden rounded-md border border-border-subtle" role="group" aria-label="view">
            {(["diff", "all", "eps"] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                onClick={() => (m === "eps" ? showEpisodes() : setMode(m))}
                className={`px-3.5 py-1.5 text-xs ${mode === m ? "bg-foreground text-surface" : "bg-surface text-foreground-muted"} ${m === "eps" ? "border-l border-border-subtle" : ""}`}
              >
                {m === "diff" ? "What changed" : m === "all" ? "Full store" : "Memories from this chapter"}
              </button>
            ))}
          </span>
          <span className="flex-1" />
          <label className="font-mono text-[10px] uppercase tracking-[0.08em] text-foreground-muted" htmlFor="hp-focus">
            Focus
          </label>
          <select
            id="hp-focus"
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
            /* the memories view is per chapter, not per belief, so the thread and
               kind filters have nothing to act on — disable rather than lie */
            disabled={mode === "eps"}
            title={mode === "eps" ? "Focus filters beliefs, not memories" : undefined}
            className="max-w-[230px] rounded-md border border-border-subtle bg-surface px-2.5 py-1.5 text-xs disabled:opacity-40"
          >
            <option value="">Everything</option>
            <optgroup label="Story arcs">
              {THREADS.map((t) => (
                <option key={t.name} value={`t:${t.name}`}>
                  {t.name}
                </option>
              ))}
            </optgroup>
            <optgroup label="Fact types">
              {["world-fact", "habit", "state"].map((k) => (
                <option key={k} value={`k:${k}`}>
                  {k}s
                </option>
              ))}
            </optgroup>
          </select>
          <span className="font-mono text-xs tabular-nums text-foreground-muted">
            {mode === "eps"
              ? `${nightEpisodes.length} ${nightEpisodes.length === 1 ? "memory" : "memories"}`
              : `${shown.length} ${shown.length === 1 ? "fact" : "facts"}`}
          </span>
        </div>
        {mode === "eps" ? (
          episodes === null ? (
            <p className="py-10 text-center text-sm text-foreground-muted">loading&hellip;</p>
          ) : (
            <div className="mt-2 divide-y divide-border-subtle">
              {nightEpisodes.map((e, i) => (
                <p key={i} className="flex flex-col gap-1 py-3 text-[13px] leading-relaxed">
                  <span className={e.used ? "" : "text-foreground-muted"}>
                    <RedactedText text={e.text} />
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.06em] tabular-nums text-foreground-muted">
                    salience {e.sal.toFixed(2)}
                    {e.used ? " · a belief was built from this" : " · no belief was ever built from this"}
                  </span>
                </p>
              ))}
            </div>
          )
        ) : shown.length ? (
          <div className="mt-2 divide-y divide-border-subtle">
            {shown.map((f) => (
              <FactRow key={f.id} f={f} night={night} focusThread={focusThread} onJump={jump} />
            ))}
          </div>
        ) : focus ? (
          <p className="mx-auto max-w-[52ch] py-10 text-center text-sm leading-relaxed text-foreground-muted">
            Nothing in this selection yet. It starts at chapter {firstChapterOfFocus ?? "17"}.
          </p>
        ) : night === 1 ? (
          <p className="mx-auto max-w-[52ch] py-10 text-center text-sm leading-relaxed text-foreground-muted">
            Nothing yet, and that is the system working. A belief is only written once something recurs
            across days, or lands hard enough to be unforgettable. Reading a chapter once, calmly, does
            neither. Step to chapter 2 and the first night of consolidation catches up with what happened here.
          </p>
        ) : (
          <p className="py-10 text-center text-sm italic text-foreground-muted">a quiet chapter, nothing recurred enough to become a fact</p>
        )}
      </div>
      <figcaption className="border-t border-border-subtle px-5 py-3.5 text-xs leading-relaxed text-foreground-muted">
        <p className="mb-3">
          Move through the book to see how facts stand after each chapter. Wording and evidence are shown as they stood then, and passages quoted from the book are redacted. Click <span className="text-foreground">distilled from&hellip;</span> on any belief to read the memories behind it.
        </p>
        {/* term on its own line, definition under it — the terms differ too much in
            width to sit in a column without the definitions going ragged */}
        <dl className="flex flex-col gap-y-2.5">
          {[
            [<span key="n" className="text-primary">new</span>, "formed after this chapter"],
            [<span key="o" className="text-destructive">overturned</span>, "later evidence contradicted it"],
            [<span key="s">salience</span>, "the weight the agent gave it as it wrote it down. A scene can matter to the plot and still score low, which is most of what goes wrong here"],
            [<span key="m">merged, completed, expired</span>, "closed without anything contradicting it, sometimes routine and sometimes how a wrong belief slips away"],
          ].map(([term, def], i) => (
            <div key={i}>
              <dt className="font-mono text-[10px] uppercase tracking-[0.06em]">{term}</dt>
              <dd className="mt-0.5 text-foreground-muted">{def}</dd>
            </div>
          ))}
        </dl>
      </figcaption>
    </figure>
  );
}
